import type { CalendarClickType, DateListCalendarProps as Props, YYYYMMDD } from '../../typings'

import { useCallback } from 'react'

import { Month } from '../../components/Month'
import { MonthNavigator } from '../../components/MonthNavigator'
import { useMonthNavigatorProps } from '../../hooks/useMonthNavigatorProps'
import { useMonths } from '../../hooks/useMonths'
import { parseDate } from '../../utils/parseDate'

import { Stack } from '@mui/material'
import { useDatesDays } from './useDatesDays'
import { useSelectionData } from './useSelectionData'

export function Dates(props: Props) {
  const {
    today,
    minDate,
    maxDate,
    dotColor,
    onChange,
    selection,
    disabledDays,
    referenceDate,
    highlightedDays,
    setReferenceDate,
    firstNonArchivedDay,
    showNeighboringMonth,
  } = props

  const months = useMonths(referenceDate, showNeighboringMonth, 1, minDate, maxDate)

  const selectionData = useSelectionData(selection)

  const daysByMonth = useDatesDays({
    selection: selectionData,
    today,
    firstNonArchivedDay,
    highlightedDays,
    disabledDays,
    months,
  })

  const monthNavigatorProps = useMonthNavigatorProps({
    referenceDate,
    setReferenceDate,
    months: 1,
    minDateAsString: minDate,
    maxDateAsString: maxDate,
    labelFormat: 'month-year',
  })

  const handleOnClick = useCallback(
    (day?: YYYYMMDD, type: CalendarClickType = 'click') => {
      if (day) {
        const dayDate = parseDate(day)
        onChange(dayDate, selection, type)
      }
    },
    [onChange, selection],
  )

  return (
    <Stack direction="column" height="auto" alignItems="center" flexShrink={0}>
      <MonthNavigator {...monthNavigatorProps} />
      <Stack direction="row" width="auto">
        {months.map(
          (month, index) =>
            daysByMonth[month.id] && (
              <Month
                index={index}
                key={month.id}
                showDaysOfWeek
                dotColor={dotColor}
                onClick={handleOnClick}
                days={daysByMonth[month.id]}
              />
            ),
        )}
      </Stack>
    </Stack>
  )
}
